/**
 * PredictionPanel - Structured prediction display (Tufte-style).
 *
 * Premium-gated: non-premium users see blurred preview with upgrade prompt.
 * Sport-agnostic: accepts generic prediction/betting data.
 * Clean grid layout with monospace numbers for alignment.
 */
import { useState, useMemo } from 'react';
import Card from '../../common/Card';
import PremiumGateModal from '../../common/PremiumGateModal';
import { useAuth } from '../../../contexts/AuthContext';

const formatOdds = (odds) => {
  if (odds == null) return '';
  return odds > 0 ? `+${odds}` : `${odds}`;
};

const formatSpread = (spread) => {
  if (spread == null) return '-';
  return spread > 0 ? `+${spread}` : `${spread}`;
};

const PredictionPanel = ({
  predictions = {},
  spread,
  spreadHomeOdds,
  spreadAwayOdds,
  overUnder,
  overOdds,
  underOdds,
  homeMoneyline,
  awayMoneyline,
  homeTeam = {},
  awayTeam = {},
}) => {
  const { userProfile } = useAuth();
  const [showPremiumModal, setShowPremiumModal] = useState(false);

  const hasPremiumAccess = userProfile?.subscription_tier === 'premium' ||
                           userProfile?.subscription_tier === 'pro' ||
                           userProfile?.is_admin;

  const { ai_predictions = [], ml, consensus } = predictions;

  // Filter to only win_loss predictions (skip total_score duplicates)
  const winLossPredictions = useMemo(() => {
    return ai_predictions.filter(p =>
      p.prediction_type === 'win_loss' || p.predicted_winner_score != null
    );
  }, [ai_predictions]);

  // Calculate consensus from win_loss predictions only
  const computedConsensus = useMemo(() => {
    const preds = winLossPredictions.filter(p => p.predicted_total != null);
    if (preds.length === 0) return consensus;

    const avgTotal = preds.reduce((sum, p) => sum + p.predicted_total, 0) / preds.length;
    const spreads = preds.filter(p => p.predicted_spread != null);
    const avgSpread = spreads.length > 0
      ? spreads.reduce((sum, p) => sum + p.predicted_spread, 0) / spreads.length
      : null;

    return {
      total: Math.round(avgTotal * 10) / 10,
      spread: avgSpread != null ? Math.round(avgSpread * 10) / 10 : null,
    };
  }, [winLossPredictions, consensus]);

  // Calculate edges
  const spreadEdge = computedConsensus?.spread != null && spread != null
    ? Math.round((Math.abs(spread) - Math.abs(computedConsensus.spread)) * 10) / 10
    : null;
  const totalEdge = computedConsensus?.total != null && overUnder != null
    ? Math.round((computedConsensus.total - overUnder) * 10) / 10
    : null;

  const hasData = winLossPredictions.length > 0 || ml;

  return (
    <>
      <Card className="p-4 relative">
        {/* Header with betting lines */}
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-xs font-semibold text-text-secondary uppercase tracking-wider">
            Predictions & Lines
          </h3>
        </div>

        {/* Betting lines as clean pills */}
        <div className="flex flex-wrap gap-2 mb-4">
          {spread != null && (
            <div className="bg-bg-secondary/50 rounded-md px-3 py-1.5">
              <div className="text-[9px] text-text-secondary uppercase">Spread</div>
              <div className="font-mono text-sm font-bold text-text-primary">
                {formatSpread(spread)}
                <span className="text-[10px] text-text-secondary font-normal ml-1">
                  ({formatOdds(spreadHomeOdds) || '-110'})
                </span>
              </div>
            </div>
          )}
          {overUnder != null && (
            <div className="bg-bg-secondary/50 rounded-md px-3 py-1.5">
              <div className="text-[9px] text-text-secondary uppercase">Over/Under</div>
              <div className="font-mono text-sm font-bold text-text-primary">
                {overUnder}
                <span className="text-[10px] text-text-secondary font-normal ml-1">
                  (O {formatOdds(overOdds) || '-110'})
                </span>
              </div>
            </div>
          )}
          {homeMoneyline != null && (
            <div className="bg-bg-secondary/50 rounded-md px-3 py-1.5">
              <div className="text-[9px] text-text-secondary uppercase">Moneyline</div>
              <div className="font-mono text-sm font-bold text-text-primary">
                {formatOdds(homeMoneyline)}
                <span className="text-[10px] text-text-secondary font-normal mx-1">/</span>
                {formatOdds(awayMoneyline)}
              </div>
            </div>
          )}
        </div>

        {/* Blurred content for non-premium users */}
        <div className={!hasPremiumAccess ? 'filter blur-md select-none pointer-events-none' : ''}>
          {hasData ? (
            <div className="space-y-3">
              {/* AI Predictions grid */}
              {winLossPredictions.length > 0 && (
                <div>
                  <div className="text-[9px] text-text-secondary uppercase tracking-wider mb-1.5">
                    AI Predictions
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
                    {winLossPredictions.map((pred, i) => {
                      const providerLabel = pred.ai_provider === 'claude' ? 'Claude'
                        : pred.ai_provider === 'openai' ? 'GPT-4'
                        : pred.ai_provider;

                      return (
                        <div
                          key={i}
                          className="bg-bg-secondary/30 border border-border/30 rounded-md px-3 py-2"
                        >
                          <div className="flex items-center justify-between mb-1">
                            <span className="text-[10px] text-text-secondary uppercase font-medium">
                              {providerLabel}
                            </span>
                            {pred.confidence != null && (
                              <span className={`font-mono text-[10px] px-1.5 py-0.5 rounded ${
                                pred.confidence >= 0.65 ? 'bg-success/10 text-success'
                                  : pred.confidence >= 0.55 ? 'bg-accent-blue/10 text-accent-blue'
                                  : 'bg-gray-500/10 text-text-secondary'
                              }`}>
                                {Math.round(pred.confidence * 100)}%
                              </span>
                            )}
                          </div>
                          <div className="font-mono text-sm font-bold text-text-primary">
                            {pred.predicted_winner && (
                              <span>{pred.predicted_winner.split(' ').pop()}</span>
                            )}
                            {pred.predicted_winner_score != null && pred.predicted_loser_score != null && (
                              <span className="ml-1">
                                {pred.predicted_winner_score}-{pred.predicted_loser_score}
                              </span>
                            )}
                          </div>
                          {pred.predicted_total != null && (
                            <div className="text-[10px] text-text-secondary mt-0.5">
                              Total: <span className="font-mono">{pred.predicted_total}</span>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* ML + Consensus row */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {/* ML Model */}
                {ml && (
                  <div className="bg-bg-secondary/30 border border-border/30 rounded-md px-3 py-2">
                    <div className="text-[9px] text-text-secondary uppercase tracking-wider mb-1">
                      ML Model
                    </div>
                    <div className="flex items-baseline gap-3">
                      {ml.win_probability != null && (
                        <div>
                          <span className="font-mono text-sm font-bold text-text-primary">
                            {Math.round(ml.win_probability * 100)}%
                          </span>
                          <span className="text-[10px] text-text-secondary ml-1">home win</span>
                        </div>
                      )}
                      {ml.total_points != null && (
                        <div>
                          <span className="font-mono text-sm font-bold text-text-primary">
                            {ml.total_points}
                          </span>
                          <span className="text-[10px] text-text-secondary ml-1">total</span>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Consensus + Edges */}
                {computedConsensus && (
                  <div className="bg-bg-secondary/30 border border-accent-blue/20 rounded-md px-3 py-2">
                    <div className="text-[9px] text-accent-blue uppercase tracking-wider mb-1 font-semibold">
                      Consensus Edge
                    </div>
                    <div className="flex items-baseline gap-4">
                      {spreadEdge != null && (
                        <div>
                          <span className="text-[9px] text-text-secondary block">Spread</span>
                          <span className={`font-mono text-sm font-bold ${
                            Math.abs(spreadEdge) >= 2 ? 'text-success' : 'text-text-primary'
                          }`}>
                            {spreadEdge > 0 ? '+' : ''}{spreadEdge}
                          </span>
                        </div>
                      )}
                      {totalEdge != null && (
                        <div>
                          <span className="text-[9px] text-text-secondary block">O/U</span>
                          <span className={`font-mono text-sm font-bold ${
                            totalEdge >= 3 ? 'text-success'
                              : totalEdge <= -3 ? 'text-danger'
                              : 'text-text-primary'
                          }`}>
                            {totalEdge > 0 ? '+' : ''}{totalEdge}
                          </span>
                        </div>
                      )}
                      {computedConsensus.total != null && (
                        <div>
                          <span className="text-[9px] text-text-secondary block">Pred Total</span>
                          <span className="font-mono text-sm font-bold text-text-primary">
                            {computedConsensus.total}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>
          ) : (
            <p className="text-xs text-text-secondary italic py-2">No predictions available for this game</p>
          )}
        </div>

        {/* Premium gate overlay */}
        {!hasPremiumAccess && hasData && (
          <div className="absolute inset-0 flex items-center justify-center rounded-xl bg-bg-card/30">
            <button
              onClick={() => setShowPremiumModal(true)}
              className="px-5 py-2.5 bg-gradient-to-r from-accent-blue/20 to-purple-500/20 border border-accent-blue/40 rounded-lg text-accent-blue text-sm font-semibold hover:from-accent-blue/30 hover:to-purple-500/30 transition-all"
            >
              Unlock AI Predictions
            </button>
          </div>
        )}
      </Card>

      <PremiumGateModal
        isOpen={showPremiumModal}
        onClose={() => setShowPremiumModal(false)}
        title="Unlock AI Predictions"
        description="Get AI-powered predictions, ML models, and edge analysis for every game."
        features={[
          'AI predictions from Claude & GPT-4',
          'ML model win probabilities & totals',
          'Spread & total edge analysis',
          'Historical prediction accuracy tracking',
        ]}
      />
    </>
  );
};

export default PredictionPanel;
